import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { withStyles, useTheme } from '@material-ui/core/styles';
import { HistoryOutlined, CloseOutlined } from '@material-ui/icons';
import { Dialog, Button, DialogActions, Typography, Grid, IconButton } from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import PropTypes from 'prop-types';
import AuditTrailContent from './content';
import { getUnreadIds } from '../../../helpers/audit-trail';
import { postJournalViews } from '../../../hooks/useAuditTrail';

const useStyles = () => ({
  auditTrail: {
    maxWidth: '600px',
    maxHeight: '600px',
    height: '100%',
    width: '100%',
  },
  auditTitleClose: {
    display: 'flex',
    selfAlignItems: 'flex-end',
  },
  contentTitle: {
    backgroundColor: '#F4F4F4',
    color: '#9C27B0',
    padding: '5px 24px',
    '& .MuiGrid-item:last-child': {
      marginLeft: 'auto',
    },
    borderBottom: '1px solid #9C27B0',
  },
  contentTitleDark: {
    backgroundColor: '#212121',
    color: '#9C27B0',
    padding: '5px 24px',
    '& .MuiGrid-item:last-child': {
      marginLeft: 'auto',
    },
    borderBottom: '1px solid #9C27B0',
  },
  contentTable: {
    borderBottom: '1px solid #E9E9E9',
  },
  auditTitleGrid: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  auditTitleCloseGrid: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  auditTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
  },
});

const DialogTitle = withStyles(useStyles)(props => {
  const { classes, onClose } = props;
  const theme = useTheme();
  return (
    <MuiDialogTitle
      disableTypography
      className={theme.palette.type === 'light' ? classes.contentTitle : classes.contentTitleDark}
    >
      <Grid container spacing={1}>
        <Grid item className={classes.auditTitleGrid}>
          <HistoryOutlined color="primary" fontSize="large" />
        </Grid>
        <Grid item xs={3} className={classes.auditTitleGrid}>
          <Typography className={classes.auditTitle}>Audit History</Typography>
        </Grid>
        <Grid item xs={1} className={classes.auditTitleCloseGrid}>
          <IconButton color="primary" onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </Grid>
      </Grid>
    </MuiDialogTitle>
  );
});

const AuditTrailModal = props => {
  const { isOpen, handleModalInteraction, eventMeta, unread, setUnread } = props;
  const { mutate } = postJournalViews();

  const queryClient = useQueryClient();

  const updateRead = currIds => {
    const data = { journalIds: currIds, eventId: eventMeta.skybox_id };
    mutate(data);
  };

  const closeModal = () => {
    handleModalInteraction();
    setUnread([]);
  };

  useEffect(() => {
    if (isOpen) {
      const ids = getUnreadIds(unread);
      if (ids && Array.isArray(ids) && ids.length > 0) {
        updateRead(ids);
      }
      queryClient.removeQueries({ queryKey: [`/journal/${eventMeta.skybox_id}`] });
    }
  }, [isOpen]);

  return (
    <div>
      <Dialog onClose={closeModal} aria-labelledby="customized-dialog-title" open={isOpen} maxWidth="md" fullWidth>
        <DialogTitle id="customized-dialog-title" onClose={closeModal} />
        <AuditTrailContent eventMeta={eventMeta} unread={unread} />
        <DialogActions>
          <Button color="primary" variant="contained" onClick={closeModal}>
            <span>Close</span>
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

AuditTrailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleModalInteraction: PropTypes.func.isRequired,
  eventMeta: PropTypes.object.isRequired,
  unread: PropTypes.array.isRequired,
  setUnread: PropTypes.func.isRequired,
};

export default AuditTrailModal;
